import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardHeader,
  CardContent,
  useTheme,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQuery } from "@apollo/client";
import ReusableTable from "../../components/ReusableTable/ReusableTable";
import InformationStepsCard from "../../components/InformationStepsCard";
import AssetDetailsDialog from "../../components/AssetDetailsDialog";
import ConfirmSnackbar from "../../components/Snackbar/ConfirmSnackbar";
import { GET_ALL_ASSETS } from "../../apis/settingsApis/assets-apis/allAssetsApis";
import {
  buildAssetColumns,
  makeRenderAssetCell,
  filterAssets,
  SECURITY_CHECKS,
  getCheckStatus,
  isAssetInScope,
  isAssetDisposed,
} from "./assetTableConfig";
import {
  CheckStatus,
  ASSETS_OUT_OF_SCOPE_EFFECT,
  inactivityReasonLabel,
} from "../../utils/enums";
import { formatTimestampParts } from "../../utils/dateUtils";
import SendAgentReminderDialog from "./SendAgentReminderDialog";
import SendFixIssuesReminderDialog from "./SendFixIssuesReminderDialog";
import AddAssetDialog from "./AddAssetDialog";
import BulkAssetScopeDialog from "./BulkAssetScopeDialog";
import ScopeActionButton from "../../components/ScopeActionButton/ScopeActionButton";

// Active, or the specific reason it is not: Disposed / Out of scope / Logged out.
// `inactivityReason` is the server's own precedence (disposed > out_of_scope >
// logged_out), so a device that is several of these at once reports the one that
// explains it best. Falls back to the flags for rows predating the field.
const assetStatusLabel = (r) => {
  const reason = inactivityReasonLabel(r.inactivityReason);
  if (reason) return reason;
  if (r.inScope === false) return "Out of scope";
  if (String(r.status ?? "").toUpperCase() === "LOGGED_OUT") return "Logged out";
  return String(r.currentStatus ?? "active").toLowerCase() === "inactive"
    ? "Inactive"
    : "Active";
};

// checkStatus reports passed/failed/disabled/total but not pending, so derive it.
// Counting the checks that actually carry a "pending" status is preferred; the
// subtraction is the fallback for rows whose `checks` array wasn't loaded.
const pendingChecksCount = (r) => {
  const status = r.checkStatus;
  if (!status) return "";
  if (status.checks?.length) {
    return status.checks.filter(
      (c) => String(c.status).toLowerCase() === CheckStatus.PENDING
    ).length;
  }
  const total = status.totalChecksCount;
  if (total == null) return "";
  const accounted =
    (status.passedChecksCount ?? 0) +
    (status.failedChecksCount ?? 0) +
    (status.disabledChecksCount ?? 0);
  return Math.max(0, total - accounted);
};

const CHECK_LABELS = {
  diskEnc: "Disk Encryption",
  antivirus: "Antivirus",
  firewall: "Firewall",
  autoUpdate: "Auto Update",
  screenLock: "Screen Lock",
};

// Every asset field surfaced anywhere in the UI (table + details dialog), for CSV export.
const EXPORT_COLUMNS = [
  { header: "Device Name", value: (r) => r.name || r.hostname || "" },
  { header: "Hostname", value: (r) => r.hostname || "" },
  { header: "Serial Number", value: (r) => r.serialNumber || "" },
  { header: "Device Type", value: (r) => r.deviceType || "" },
  { header: "Model", value: (r) => r.model || "" },
  { header: "Platform", value: (r) => r.platform || "" },
  { header: "OS Name", value: (r) => r.osName || "" },
  { header: "OS Version", value: (r) => r.osVersion || "" },
  { header: "Owner Name", value: (r) => r.ownerName || "" },
  { header: "Owner Email", value: (r) => r.ownerEmail || "" },
  { header: "Source", value: (r) => (r.source === "WATCH_TOWER" ? "Rego WatchTower" : r.integrationName || "MDM") },
  // The export spans both tabs, so it has to carry the distinction the tabs were
  // making — otherwise in-scope and out-of-scope devices are indistinguishable
  // once they are in the same sheet.
  { header: "Compliance Scope", value: (r) => (isAssetInScope(r) ? "In Scope" : "Out of Scope") },
  { header: "Scope Reason", value: (r) => r.scopeReason || "" },
  // "Inactive" on its own told the reader nothing — a disposed device, a
  // logged-out one and an admin exclusion all landed on the same word. Report the
  // reason instead, which is what someone reading the sheet actually needs.
  { header: "Current Status", value: (r) => assetStatusLabel(r) },
  { header: "Last Sync", value: (r) => formatTimestampParts(r.lastActivityAt).full || "" },
  { header: "Disposal Date", value: (r) => r.disposableDate || "" },
  { header: "Description", value: (r) => r.description || "" },
  ...SECURITY_CHECKS.map((chk) => ({
    header: CHECK_LABELS[chk.field] || chk.field,
    value: (r) => getCheckStatus(r, chk),
  })),
];

// Kept separate from EXPORT_COLUMNS so the per-tenant check columns can be
// slotted in ahead of them: every individual check reads across together, then
// the totals. Order is ... Screen Lock | <catalog checks> | Checks Passed ...
const EXPORT_CHECK_COUNT_COLUMNS = [
  { header: "Checks Passed", value: (r) => r.checkStatus?.passedChecksCount ?? "" },
  { header: "Checks Failed", value: (r) => r.checkStatus?.failedChecksCount ?? "" },
  // Not a server-side count like the others — checkStatus carries passed/failed/
  // disabled/total only, so pending is whatever the catalog has not reported on.
  { header: "Checks Pending", value: (r) => pendingChecksCount(r) },
  { header: "Checks Disabled", value: (r) => r.checkStatus?.disabledChecksCount ?? "" },
  { header: "Checks Total", value: (r) => r.checkStatus?.totalChecksCount ?? "" },
];


const AssetsHome = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [reminderMenuAnchor, setReminderMenuAnchor] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [agentReminderOpen, setAgentReminderOpen] = useState(false);
  const [fixIssuesReminderOpen, setFixIssuesReminderOpen] = useState(false);
  const [tableFilters, setTableFilters] = useState({});
  const [checkedRows, setCheckedRows] = useState(new Set());
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [bulkScope, setBulkScope] = useState({ open: false, mode: "out" });
  const [sortBy, setSortBy] = useState([]); // e.g. ["-activity"] desc, ["activity"] asc
  const [scopeTab, setScopeTab] = useState("active"); // "active" | "inactive"

  const { data, loading, refetch } = useQuery(GET_ALL_ASSETS, {
    fetchPolicy: "cache-and-network",
  });

  const rows = useMemo(() => data?.getAllAssets ?? [], [data]);

  // Keep an open details dialog pointed at the freshest row after a refetch,
  // so its status chip/fields reflect saved changes (matched by assetId).
  useEffect(() => {
    setSelectedAsset((prev) => {
      if (!prev) return prev;
      const key = prev.assetId || prev.id;
      return rows.find((r) => (r.assetId || r.id) === key) || prev;
    });
  }, [rows]);

  const ownerOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      const key = r.ownerEmail || r.ownerName;
      if (key && !seen.has(key)) seen.set(key, r.ownerName || r.ownerEmail);
    });
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [rows]);

  const sourceOptions = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => {
      const label = r.source === "WATCH_TOWER" ? "Rego WatchTower" : r.integrationName || "MDM";
      seen.add(label);
    });
    return [...seen].map((label) => ({ id: label, label }));
  }, [rows]);

  // handleFilterChange matches people page: receives full filter object from ReusableTable filterManager
  const handleFilterChange = useCallback((filters) => {
    setTableFilters(filters);
  }, []);

  const filteredRows = useMemo(() => {
    let result = filterAssets(rows, searchQuery);

    if (tableFilters.owner?.length) {
      result = result.filter((r) =>
        tableFilters.owner.includes(r.ownerEmail || r.ownerName || "")
      );
    }

    SECURITY_CHECKS.forEach((chk) => {
      if (!tableFilters[chk.field]?.length) return;
      result = result.filter((r) => tableFilters[chk.field].includes(getCheckStatus(r, chk)));
    });

    if (tableFilters.managed?.length) {
      result = result.filter((r) => {
        const label = r.source === "WATCH_TOWER" ? "Rego WatchTower" : r.integrationName || "MDM";
        return tableFilters.managed.includes(label);
      });
    }

    return result;
  }, [rows, searchQuery, tableFilters]);

  // Client-side sort (currently Last Sync only). sortBy is controlled by the table header.
  const sortedRows = useMemo(() => {
    const entry = sortBy[0];
    if (!entry) return filteredRows;
    const desc = entry.startsWith("-");
    const field = desc ? entry.slice(1) : entry;
    if (field !== "activity") return filteredRows;
    const toTime = (v) => {
      const t = v ? new Date(v).getTime() : 0;
      return Number.isNaN(t) ? 0 : t;
    };
    return [...filteredRows].sort((a, b) => {
      const diff = toTime(a.lastActivityAt) - toTime(b.lastActivityAt);
      return desc ? -diff : diff;
    });
  }, [filteredRows, sortBy]);

  // Rows for the selected scope, keeping the current sort order.
  const scopedRows = useMemo(
    () =>
      sortedRows.filter((r) =>
        scopeTab === "inactive" ? !isAssetInScope(r) : isAssetInScope(r)
      ),
    [sortedRows, scopeTab]
  );

  const handleScopeChange = (_e, next) => {
    if (!next || next === scopeTab) return;
    setScopeTab(next);
    setCheckedRows(new Set()); // selection is per-tab
  };

  const deviceCount = scopedRows.length;

  const selectableRows = scopedRows;

  const rowKey = (row) => row.assetId || row.id;

  const allChecked = selectableRows.length > 0 && selectableRows.every((r) => checkedRows.has(rowKey(r)));
  const someChecked = selectableRows.some((r) => checkedRows.has(rowKey(r)));

  const toggleAllChecked = () => {
    if (allChecked) {
      setCheckedRows(new Set());
    } else {
      setCheckedRows(new Set(selectableRows.map(rowKey)));
    }
  };

  const toggleRowChecked = (row) => {
    const key = rowKey(row);
    setCheckedRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const checkedAssets = useMemo(
    () => selectableRows.filter((r) => checkedRows.has(rowKey(r))),
    [selectableRows, checkedRows]
  );

  const enrolledOwnerEmails = useMemo(
    () => rows.map((r) => r.ownerEmail).filter(Boolean),
    [rows]
  );

  // With rows selected, use them. Otherwise the menu promises to notify "all the
  // asset owners with failing checks", so target every in-scope device with a
  // failing check — not just the ones on the open tab.
  //
  // It used to read from `selectableRows`, which is the current tab. An owner
  // whose failing device sat on the other tab from the one being viewed was
  // silently left out of a send that claimed to cover everyone. Scoped from
  // `sortedRows` instead, so search and column filters still narrow it (those are
  // deliberate) but the tab does not (it is only a view).
  //
  // Restricted to in-scope devices because the API refuses to notify owners of
  // out-of-scope ones anyway — including them would just add silent no-ops.
  const assetsForFixReminder = useMemo(() => {
    if (checkedRows.size > 0) return checkedAssets;
    // Pending counts alongside failing: a check sits pending when the device has
    // never reported a result, which only the owner can resolve by opening the
    // agent. The API agrees — its fetchFailingChecks treats both as actionable.
    return sortedRows.filter(
      (r) =>
        isAssetInScope(r) &&
        SECURITY_CHECKS.some((chk) => {
          const status = getCheckStatus(r, chk);
          return status === CheckStatus.FAIL || status === CheckStatus.PENDING;
        })
    );
  }, [checkedAssets, checkedRows.size, sortedRows]);

  // The five checks the table shows are hardcoded, but the check catalog is
  // per-tenant data — `checkStatus.checks` carries every active check, whatever
  // it is called. Build a column for each one the hardcoded set doesn't already
  // cover, so a check configured server-side (Hardware Inventory, Gatekeeper, …)
  // reaches the export without this file needing to know it exists.
  const extraCheckColumns = useMemo(() => {
    const known = new Set(SECURITY_CHECKS.map((c) => c.checkType));
    const seen = new Map();
    rows.forEach((r) => {
      (r.checkStatus?.checks || []).forEach((c) => {
        const key = c.checkType || String(c.activeCheckId || "");
        if (!key || known.has(key) || seen.has(key)) return;
        seen.set(
          key,
          c.activeCheckName ||
            key.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase())
        );
      });
    });
    return [...seen.entries()].map(([key, label]) => ({
      header: label,
      value: (r) =>
        r.checkStatus?.checks?.find(
          (c) => (c.checkType || String(c.activeCheckId || "")) === key
        )?.status || "",
    }));
  }, [rows]);

  const handleExport = () => {
    const exportColumns = [
      ...EXPORT_COLUMNS,
      ...extraCheckColumns,
      ...EXPORT_CHECK_COUNT_COLUMNS,
    ];
    // With nothing selected, export every device — both tabs. The In Scope /
    // Out of Scope toggle is a view, not a filter on the data, and exporting
    // only the tab that happened to be open silently dropped half the fleet.
    // Search and column filters still apply, since those *are* the user
    // narrowing the set deliberately.
    const targets = checkedRows.size > 0
      ? selectableRows.filter((r) => checkedRows.has(rowKey(r)))
      : sortedRows;
    const csvRows = [
      exportColumns.map((c) => c.header),
      ...targets.map((r) => exportColumns.map((c) => c.value(r))),
    ];
    const csv = csvRows
      .map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `assets${checkedRows.size > 0 ? `_${checkedRows.size}_selected` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(
    () => [
      {
        label: (
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked && !allChecked}
            onChange={toggleAllChecked}
            size="small"
            sx={{ p: 0 }}
          />
        ),
        field: "checkbox",
        width: "4%",
        align: "center",
      },
      // Out of Scope = inactive assets, which aren't monitored — drop the check columns.
      ...buildAssetColumns({
        showOwner: true,
        ownerOptions,
        sourceOptions,
        showChecks: scopeTab === "active",
        reservedWidth: 9, // the checkbox column above (4%) + the action column below (5%)
      }),
      { label: "", field: "action", width: "5%", align: "left" },
    ],
    [ownerOptions, sourceOptions, allChecked, someChecked, toggleAllChecked, theme, scopeTab]
  );

  const renderAssetCell = useMemo(() => makeRenderAssetCell(theme), [theme]);

  const renderCell = (row, column) => {
    if (column.field === "checkbox") {
      const key = rowKey(row);
      return (
        <Checkbox
          size="small"
          checked={checkedRows.has(key)}
          onChange={() => toggleRowChecked(row)}
          onClick={(e) => e.stopPropagation()}
          sx={{ p: 0 }}
        />
      );
    }

    if (column.field === "action") {
      return (
        <Tooltip title="View details" arrow>
          <IconButton
            size="small"
            onClick={() => setSelectedAsset(row)}
            sx={{
              color: theme.customColors.icons.dark,
              "&:hover": {
                backgroundColor: theme.customColors.background.light,
                transform: "translateX(-2px)",
                transition: "all 0.2s ease",
              },
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      );
    }

    return renderAssetCell(row, column);
  };

  // Both reminders can be meaningless for what is currently selected, and a menu
  // item that silently sends nothing is worse than one that says why it can't.
  //
  // Derived from the rows rather than from `scopeTab` so it stays correct if
  // selection ever stops being cleared on tab change.
  // Only an explicit out-of-scope selection blocks the fix reminder. With nothing
  // selected it targets every in-scope device with a failing check regardless of
  // the open tab, so sitting on the Out of Scope tab is not a reason to disable.
  const targetingOutOfScope = checkedAssets.some((r) => !isAssetInScope(r));


  const agentReminderIgnoresSelection = checkedRows.size > 0;

  // Out-of-scope devices are not checked, so there are no failing checks to chase.
  const fixReminderDisabledReason = targetingOutOfScope
    ? "Your selection includes out of scope devices, which are not checked and whose owners are never notified. Clear the selection or pick in scope devices only."
    : assetsForFixReminder.length === 0
      ? checkedRows.size > 0
        ? "None of the devices you have selected have a failing check, so there is nobody to notify."
        : "No in scope device has a failing check, so there is nobody to notify."
      : "";

  // Restoring a disposed device cannot work: the disposal date is what holds it
  // out of scope, so flipping the flag would leave it exactly where it was. The
  // details dialog blocks the same move for the same reason — there, the toggle
  // is replaced by a read-only "Out of Scope · Disposed" chip.
  const markingInScope = scopeTab !== "active";
  const selectionHasDisposed = checkedAssets.some(isAssetDisposed);
  const bulkScopeDisabledReason =
    checkedRows.size === 0
      ? "Select devices first"
      : markingInScope && selectionHasDisposed
        ? "Your selection includes disposed devices, which stay out of scope until their disposal date is cleared. Open each one to clear it, or deselect them."
        : "";

  const handleReminderMenuOpen = (e) => setReminderMenuAnchor(e.currentTarget);
  const handleReminderMenuClose = () => setReminderMenuAnchor(null);

  return (
    <Box sx={{ width: "100%", pl: 0, pr: 0, ml: 0, mr: 0, mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page shows all the devices across your organization — from Rego WatchTower and your connected MDM."
          steps={[
            "Each row is one device entry. The same physical device may appear multiple times if it's seen by both Rego WatchTower and an MDM — each source shows its own row.",
            "The list has two tabs. **In Scope** is the devices that come under your organization's compliance area — these are the ones being monitored and reported on.",
            `**Out of Scope** is the devices that do not. ${ASSETS_OUT_OF_SCOPE_EFFECT}`,
            "Select devices with the checkboxes, then use **Mark Out of Scope** to exclude them — you'll be asked for a reason.",
            "Open a device and set a **Disposal Date** to retire it. Once that date is reached, the device moves out of your organization's compliance scope automatically and no compliance checks run for it any more.",
            "Open a device to see its full security checks — disk encryption, firewall, antivirus, screen lock and more.",
            "Owner is editable **only on manually added devices**. For MDM devices, change it in the source and it updates here on the next sync (within 24 hours); for Rego WatchTower it is fixed to whoever registered with the enrollment token. 'Unassigned' means no owner is known yet.",
            "Select devices using the checkboxes, then use 'Send Reminder' to notify owners to fix issues.",
            "If your organization does not use an external MDM, enable Rego WatchTower from the Integrations page. Once enabled, the **Connect Device** button will appear, allowing employees to enroll their devices through Rego WatchTower from the [Employee Assets Page](/employee/assets) ."
          ]}
          footerText="💡 Employees install WatchTower from their Assets page; MDM devices sync automatically."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="h5" fontWeight="600" color={theme.customColors.text.strong}>
                Assets
              </Typography>
              <ToggleButtonGroup
                value={scopeTab}
                exclusive
                size="small"
                onChange={handleScopeChange}
                aria-label="asset status filter"
                sx={{
                  borderRadius: "26px",
                  border: "1px solid #E8EAED",
                  gap: 0.2,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: "36px",
                    color: "#6B7280",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    px: 3,
                    py: 1,
                    minWidth: "70px",
                    transition: "all 0.2s ease-in-out",
                    "&.Mui-selected": {
                      backgroundColor: "#3B82F6",
                      color: "#fff",
                      boxShadow: "inset 0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                      "&:hover": { backgroundColor: "#2563EB" },
                    },
                    "&:hover:not(.Mui-selected)": { backgroundColor: "#F9FAFB", color: "#374151" },
                  },
                }}
              >
                <ToggleButton value="active">In Scope</ToggleButton>
                <ToggleButton value="inactive">Out of Scope</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            ".MuiCardHeader-title": { fontSize: "16px", fontWeight: "bold" },
            padding: "14px",
            minHeight: 88,
            boxSizing: "border-box",
            "& .MuiCardHeader-action": {
              m: 0,
              minHeight: 40,
              display: "flex",
              alignItems: "center",
            },
          }}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => setAddAssetOpen(true)}
                sx={{
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "14px",
                  px: 2,
                  py: 1,
                  borderColor: theme.customColors.border.main,
                  color: theme.customColors.text.strong,
                  "&:hover": {
                    borderColor: theme.customColors.border.strong,
                    backgroundColor: theme.customColors.background.hover,
                  },
                }}
              >
                Add Asset
              </Button>

              <Button
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={handleExport}
                sx={{
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "14px",
                  px: 2,
                  py: 1,
                  borderColor: theme.customColors.border.main,
                  color: theme.customColors.text.strong,
                  "&:hover": {
                    borderColor: theme.customColors.border.strong,
                    backgroundColor: theme.customColors.background.hover,
                  },
                }}
              >
                Export{checkedRows.size > 0 ? ` (${checkedRows.size})` : ""}
              </Button>
              
                      
              <Button
                variant="contained"
                startIcon={<NotificationsIcon sx={{ fontSize: 16 }} />}
                endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
                onClick={handleReminderMenuOpen}
                sx={{
                  bgcolor: theme.customColors.button.primary,
                  color: theme.customColors.button.text,
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "14px",
                  px: 2,
                  py: 1,
                  "&:hover": { bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary },
                }}
              >
                Send Reminder{checkedRows.size > 0 ? ` (${checkedRows.size})` : ""}
              </Button>

              {/* Direction follows the tab, like the People list: exclude from the
                  In Scope view, restore from the Out of Scope one. */}
              <Tooltip
                title={bulkScopeDisabledReason}
                arrow
                disableHoverListener={!bulkScopeDisabledReason}
              >
                <span>
                  <ScopeActionButton
                    mode={scopeTab === "active" ? "out" : "in"}
                    onClick={() =>
                      setBulkScope({
                        open: true,
                        mode: scopeTab === "active" ? "out" : "in",
                      })
                    }
                    disabled={Boolean(bulkScopeDisabledReason)}
                  />
                </span>
              </Tooltip>

              {checkedRows.size > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setCheckedRows(new Set())}
                  sx={{
                    fontWeight: 500,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "14px",
                    px: 2,
                    py: 1,
                    borderColor: theme.customColors.border.main,
                    color: theme.customColors.text.strong,
                    "&:hover": { borderColor: theme.customColors.border.strong, backgroundColor: theme.customColors.background.hover },
                  }}
                >
                  Clear
                </Button>
              )}

              <Menu
                anchorEl={reminderMenuAnchor}
                open={Boolean(reminderMenuAnchor)}
                onClose={handleReminderMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 260,
                    boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -2px rgba(0,0,0,0.05)",
                    borderRadius: "8px",
                    border: "1px solid #E5E7EB",
                  },
                }}
              >
                {/* The span is what the tooltip hangs off: a disabled MenuItem
                    receives no pointer events, and the disabled case is exactly
                    when the explanation is needed. */}
                <MenuItem
                  onClick={() => { handleReminderMenuClose(); setAgentReminderOpen(true); }}
                  sx={{ py: 1.5, px: 2, "&:hover": { backgroundColor: theme.customColors.background.light } }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600} color={theme.customColors.text.strong}>
                      Rego WatchTower Agent Installation
                    </Typography>
                    <Typography variant="caption" color={theme.customColors.text.secondary} sx={{ display: "block", mt: 0.25 }}>
                      {agentReminderIgnoresSelection
                        ? "For employees without a connected device — your selection is ignored, pick them in the next step"
                        : "For employees without a connected device"}
                    </Typography>
                  </Box>
                </MenuItem>
                <Tooltip title={fixReminderDisabledReason} arrow placement="left">
                  <span>
                    <MenuItem
                      onClick={() => { handleReminderMenuClose(); setFixIssuesReminderOpen(true); }}
                      disabled={Boolean(fixReminderDisabledReason)}
                      sx={{ py: 1.5, px: 2, "&:hover": { backgroundColor: theme.customColors.background.light } }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600} color={theme.customColors.text.strong}>
                          Failing Security Checks
                          {checkedRows.size > 0 ? ` (${checkedRows.size} selected)` : ""}
                        </Typography>
                        <Typography variant="caption" color={theme.customColors.text.secondary} sx={{ display: "block", mt: 0.25 }}>
                          {assetsForFixReminder.length === 0
                            ? "No device with a failing check"
                            : checkedRows.size > 0
                              ? "Notify selected device owners"
                              : "Notify all the asset owners with failing checks"}
                        </Typography>
                      </Box>
                    </MenuItem>
                  </span>
                </Tooltip>
              </Menu>
            </Box>
          }
        />

        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              pb: 1.5,
              backgroundColor: theme.customColors.background.main,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#6B7280", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                          edge="end"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      transition: "all 0.2s ease",
                      fontSize: "14px",
                      "& fieldset": { borderColor: "#E5E7EB" },
                      "&:hover fieldset": {
                        borderColor: "#9CA3AF",
                        boxShadow: "0 0 0 3px rgba(59,130,246,0.1)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3B82F6",
                        boxShadow: "0 0 0 3px rgba(59,130,246,0.1)",
                      },
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  minWidth: 120,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: "10px",
                    backgroundColor: "#F3F4F6",
                    border: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="#374151"
                    fontWeight="500"
                    sx={{ lineHeight: 1.2 }}
                  >
                    {deviceCount} {deviceCount === 1 ? "device" : "devices"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box>
            <ReusableTable
              columns={columns}
              data={scopedRows}
              renderCell={renderCell}
              onFilterChange={handleFilterChange}
              filters={tableFilters}
              sortBy={sortBy}
              onSortChange={setSortBy}
              loading={loading}
              rowsPerPageOptions={[10, 50, 100]}
              rowsPerPage={50}
              getRowSx={(row) => {
                const key = rowKey(row);
                return checkedRows.has(key) ? { bgcolor: "rgba(3,27,137,0.05)" } : {};
              }}
              getRowProps={() => ({})}
              // The checkbox and action cells hold a single control and no text,
              // so disable the ellipsis there — otherwise the control overflows
              // the narrow cell and the browser renders a stray "…".
              getCellSx={(row, column) =>
                column.field === "checkbox" || column.field === "action"
                  ? { textOverflow: "clip" }
                  : {}
              }
              sx={{
                "& .MuiTableCell-root": {
                  py: "12px !important",
                  px: "10px !important",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
                "& .MuiTableRow-root:hover": { bgcolor: "rgba(0,0,0,0.01)" },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <AddAssetDialog
        open={addAssetOpen}
        onClose={() => setAddAssetOpen(false)}
        refetch={refetch}
      />

      <BulkAssetScopeDialog
        open={bulkScope.open}
        mode={bulkScope.mode}
        onClose={() => setBulkScope((prev) => ({ ...prev, open: false }))}
        assets={checkedAssets}
        refetch={refetch}
        // Re-scoped devices move to the other tab, so the selection no longer
        // points at anything visible here.
        onSaveSuccess={() => setCheckedRows(new Set())}
      />

      <AssetDetailsDialog
        open={!!selectedAsset}
        asset={selectedAsset}
        onClose={() => setSelectedAsset(null)}
        onSaved={refetch}
      />

      <SendAgentReminderDialog
        open={agentReminderOpen}
        onClose={() => setAgentReminderOpen(false)}
        onSuccess={(msg) => setSnackbarInfo({ message: msg, vertical: "top", horizontal: "right" })}
        enrolledOwnerEmails={enrolledOwnerEmails}
      />

      <SendFixIssuesReminderDialog
        open={fixIssuesReminderOpen}
        onClose={() => setFixIssuesReminderOpen(false)}
        onSuccess={(msg) => setSnackbarInfo({ message: msg, vertical: "top", horizontal: "right" })}
        selectedAssets={assetsForFixReminder}
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default AssetsHome;
